import type { PropType } from "vue";
import type { Size } from "@/components/helpers/common-props";
import type { FlexComponent } from "@/utils/render-component";
import { computed, defineComponent, ref } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { renderComponent } from "@/utils/render-component";
import Dropdown from "../../primitives/dropdown/Dropdown";
import Popover from "../../primitives/popover/Popover";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import Separator from "../separator/Separator";

export default defineComponent({
  name: "FacetedFilter",
  props: {
    label: p.$string(),
    icon: p.string("radix-icons:plus-circled"),
    badges: {
      type: [String, Object] as PropType<FlexComponent>,
    },
    size: p.string<Size | "full">("sm"),
    type: p.string<"dropdown" | "popover">("popover"),
    active: p.boolean(),
  },
  setup(props, { slots, expose }) {
    const open = ref(false);
    const styles = computed(() => {
      if (props.size === "xs") return "min-w-[160px]";
      if (props.size === "sm") return "min-w-[200px]";
      if (props.size === "md") return "min-w-[280px]";
      if (props.size === "lg") return "min-w-[360px]";
      return "w-full";
    });

    const icon = computed<string>(() => {
      if (props.active) return "radix-icons:minus-circled";
      return props.icon;
    });

    const Component = props.type === "dropdown" ? Dropdown : Popover;

    expose({
      close: () => {
        open.value = false;
      },
    });

    return () => (
      <Component.Root v-model:open={open.value}>
        <Component.Trigger as-child>
          <Button
            variant={props.active ? "outline" : "dashed"}
            class={["h-8 pl-3", props.badges ? "pr-2" : "pr-4"]}
            // icon={icon.value}
            //@ts-expect-error
            onKeydown={(e) => {
              if (["ArrowDown", "ArrowUp"].includes(e.code)) {
                open.value = true;
              }
            }}
          >
            <span class="center space-x-1">
              <Icon icon={icon.value} class="mr-2 h-4 w-4 opacity-100" />
              {props.label}
              {props.badges && (
                <span class="center">
                  <Separator orientation="vertical" class="ml-3 mr-2 h-4" />

                  {renderComponent(props.badges)}
                </span>
              )}
            </span>
          </Button>
          <Component.Content loop class={cn("overflow-hidden p-0", styles.value)} align="start">
            {slots.default?.()}
          </Component.Content>
        </Component.Trigger>
      </Component.Root>
    );
  },
});
